import { useEffect, useMemo, useState } from 'react';
import { Scroll, RefreshCw, Filter, User, Gavel } from 'lucide-react';
import supabase from '../lib/supabase';
import { PageHeader, Section, Badge, Button, EmptyState, Input, Select } from '../components/ui';

type Row = {
  id: number;
  actor_user_id: string | null;
  actor_email: string | null;
  action: string;
  target_type: string | null;
  target_id: string | null;
  metadata: Record<string, any>;
  ip_address: string | null;
  created_at: string;
};

/**
 * Read-only view of admin_audit_log. Filter by action, actor, or target.
 * Sorted latest-first. No UI to delete or mutate — the table only
 * permits SELECT via RLS.
 */
export default function AuditLog() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState<string>('');
  const [search, setSearch] = useState<string>('');

  const load = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('admin_audit_log')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(500);
    setRows((data as Row[]) ?? []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  // Distinct action values for the dropdown.
  const actions = useMemo(() => {
    const s = new Set(rows.map((r) => r.action));
    return Array.from(s).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (actionFilter && r.action !== actionFilter) return false;
      if (!q) return true;
      return (
        r.actor_email?.toLowerCase().includes(q)
        || r.target_id?.toLowerCase().includes(q)
        || r.target_type?.toLowerCase().includes(q)
        || JSON.stringify(r.metadata).toLowerCase().includes(q)
      );
    });
  }, [rows, actionFilter, search]);

  return (
    <div className="p-8 max-w-6xl">
      <PageHeader
        eyebrow="AceRent admin"
        title="Audit log"
        subtitle="Append-only record of admin actions on the platform. Rows cannot be edited or deleted."
        actions={<Button variant="secondary" icon={RefreshCw} loading={loading} onClick={load}>Refresh</Button>}
      />

      <div className="flex gap-2 flex-wrap mb-4">
        <div className="relative flex-1 min-w-[240px] max-w-sm">
          <Filter className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search actor email, target id, or metadata"
            className="pl-9"
          />
        </div>
        <Select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} className="w-56">
          <option value="">All actions</option>
          {actions.map((a) => <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>)}
        </Select>
      </div>

      <Section
        title={`${filtered.length} event${filtered.length === 1 ? '' : 's'}`}
        subtitle={rows.length === 500 ? 'Showing the most recent 500.' : undefined}
        className="p-0 overflow-hidden"
      >
        {loading ? (
          <div className="p-10 text-center text-sm text-gray-500">Loading audit entries…</div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Scroll}
            title="No audit events match"
            description="Change filters, or take a destructive admin action (refund, role change) and it'll appear here."
          />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50/70 text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">When</th>
                <th className="text-left px-5 py-3 font-semibold">Actor</th>
                <th className="text-left px-5 py-3 font-semibold">Action</th>
                <th className="text-left px-5 py-3 font-semibold">Target</th>
                <th className="text-left px-5 py-3 font-semibold">Metadata</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50/60 align-top">
                  <td className="px-5 py-3 whitespace-nowrap text-gray-700">
                    {new Date(r.created_at).toLocaleString('en-GB', {
                      day: '2-digit', month: 'short', year: 'numeric',
                      hour: '2-digit', minute: '2-digit', second: '2-digit',
                    })}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <User className="w-3.5 h-3.5 text-gray-400" />
                      <div className="text-gray-900">{r.actor_email ?? '—'}</div>
                    </div>
                    {r.ip_address && (
                      <div className="text-[11px] text-gray-500 mt-0.5 font-mono">{r.ip_address}</div>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <Badge tone={toneForAction(r.action)}>{r.action.replace(/_/g, ' ')}</Badge>
                  </td>
                  <td className="px-5 py-3">
                    {r.target_type ? (
                      <>
                        <div className="text-xs text-gray-500 uppercase tracking-wider">{r.target_type}</div>
                        <div className="text-[11px] font-mono text-gray-700 break-all">{r.target_id}</div>
                      </>
                    ) : '—'}
                  </td>
                  <td className="px-5 py-3">
                    {Object.keys(r.metadata ?? {}).length > 0 ? (
                      <pre className="text-[11px] font-mono text-gray-700 whitespace-pre-wrap break-all bg-gray-50 rounded p-2 max-w-md">
                        {JSON.stringify(r.metadata, null, 2)}
                      </pre>
                    ) : <span className="text-gray-400">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
    </div>
  );
}

function toneForAction(a: string): 'green' | 'amber' | 'red' | 'blue' | 'gray' {
  if (/granted|approved|unblocked/.test(a))                          return 'green';
  if (/revoked|rejected|blocked|deleted|refund/.test(a))             return 'red';
  if (/role|settings|updated/.test(a))                               return 'amber';
  return 'blue';
}
